import React, { useEffect, useRef, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import {
  Box,
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react'

import { useArchiveCustomSummaryPrompt } from '@/react-query/interview/interview'
import { DeleteIcon } from '@/views/assets/DeleteIcon'

import { PromptTemplate } from './CustomPromptsLibrary'

const MotionIconButton = motion(IconButton)

type CustomPromptButtonProps = {
  template: PromptTemplate & { id?: string }
  onSelectTemplate: (template: PromptTemplate) => void
  isDeletable?: boolean
}

export const CustomPromptButton = ({
  template,
  onSelectTemplate,
  isDeletable = true,
}: CustomPromptButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const archivePrompt = useArchiveCustomSummaryPrompt()
  const [showDeleteIcon, setShowDeleteIcon] = useState(false)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = () => {
    if (!isDeletable || !template.id) return

    hoverTimeoutRef.current = setTimeout(() => {
      setShowDeleteIcon(true)
    }, 500)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setShowDeleteIcon(false)
  }

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const handleArchive = async (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!template.id) return

    archivePrompt.mutate(template.id)
    onClose()
  }

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onOpen()
  }

  return (
    <>
      <Button
        variant={'brand-solid-gray'}
        flexShrink={0}
        onClick={() => onSelectTemplate(template)}
        position="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {template.name}
        {template.hasScoring && (
          <Box
            ml={2}
            width={'16px'}
            height={'6px'}
            backgroundColor={'#009900'}
            rounded={'full'}
            display={'inline-block'}
          />
        )}
        <AnimatePresence>
          {isDeletable && template.id && showDeleteIcon && (
            <MotionIconButton
              aria-label={`Delete prompt ${template.name}`}
              icon={<DeleteIcon w={3} h={3} />}
              rounded={'full'}
              minW={6}
              minH={6}
              h={6}
              w={6}
              boxShadow={'0px 1px 3px 0px rgba(0, 0, 0, 0.25)'}
              bg={'#f5f5f5'}
              position="absolute"
              top={-2}
              right={-2}
              variant={'brand-solid-white'}
              onClick={handleDeleteClick}
              zIndex={20}
              _hover={{ bg: '#e0e0e0' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
          )}
        </AnimatePresence>
      </Button>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete custom topic</ModalHeader>
          <ModalCloseButton
            onClick={(event) => {
              event.stopPropagation()
              onClose()
            }}
          />
          <ModalBody>
            Are you sure you want to delete <b>{template.name}</b>?
          </ModalBody>
          <ModalFooter>
            <Button
              rounded={'1.25rem'}
              colorScheme={'red'}
              onClick={handleArchive}
              isLoading={archivePrompt.isPending}
            >
              Delete
            </Button>
            <Button
              onClick={(event) => {
                event.stopPropagation()
                onClose()
              }}
              rounded={'1.25rem'}
              variant="brand-solid-white"
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
